import type {StackEntryPayload, StackEntryServerStatus} from '../../../page-data/payloads/merge-box'
import type {StackMergeabilityPageData} from '../../../page-data/payloads/stack-mergeability'
import type {UseQueryResult} from '@github-ui/react-query'
import type {StackSectionStackEntry} from '../stack-section-types'
import {EffectiveEntryStatus} from '../../effective-entry-status'

// Non-dynamic statuses whose effectiveEntryStatus is fixed by the `merge_box`
// payload itself and never comes from `stack_mergeability` query data. These
// always win over query data (e.g. an AFTER entry stays 'AFTER').
const STATIC_STATUS_BY_SERVER_STATUS: Partial<Record<StackEntryServerStatus, EffectiveEntryStatus>> = {
  IRRELEVANT_MERGED: EffectiveEntryStatus.Merged,
  IRRELEVANT_AFTER: EffectiveEntryStatus.After,
  IRRELEVANT_DRAFT: EffectiveEntryStatus.Draft,
  IRRELEVANT_QUEUED: EffectiveEntryStatus.Queued,
}

// Placeholder statuses used only while we wait for query data to arrive.
const SEED_PLACEHOLDER_BY_SERVER_STATUS: Partial<Record<StackEntryServerStatus, EffectiveEntryStatus>> = {
  BLOCKED_STACK: EffectiveEntryStatus.NotReady,
  NEEDS_MERGE_STATE: EffectiveEntryStatus.Loading,
}

// The fixed status for an entry, or undefined when the status is dynamic and
// should come from query data. A closed PR is always 'CLOSED', and an AFTER
// entry (stacked on top of CURRENT) is always 'AFTER'.
function staticEffectiveStatus(entry: StackEntryPayload): EffectiveEntryStatus | undefined {
  if (entry.position === 'AFTER') return EffectiveEntryStatus.After
  if (entry.pull.state === 'DRAFT') return EffectiveEntryStatus.Draft
  if (entry.pull.state === 'CLOSED') return EffectiveEntryStatus.Closed
  const byServerStatus = STATIC_STATUS_BY_SERVER_STATUS[entry.status]
  if (byServerStatus) return byServerStatus
  return undefined
}

/**
 * Hydrates the stack entries that come from the `merge_box` API by combining
 * them, by index, with the matching `stack_mergeability` query data (when it
 * exists). For each entry this does 3 things:
 *
 * 1. Convert each Stack Entry from a type of StackEntryPayload (from `merge_box` API) -> StackSectionStackEntry
 * 2. Merge the `.channels` and `.statusCheckChannels` fields from the `stack_mergeability`
 *    query data into a single AliveChannels object (null until that data arrives)
 * 3. Sets an effectiveEntryStatus. Non-dynamic statuses (closed, draft, merged,
 *    queued, after) come straight from the `merge_box` entry and always win over
 *    query data; otherwise it uses the `stack_mergeability` query data when
 *    present, falling back to a placeholder while that data loads.
 */
export function hydrateMergeBoxStackEntriesWithStackMergeabilityData(
  stackMergeabilityQueries: Array<UseQueryResult<StackMergeabilityPageData>>,
): (mergeBoxStackEntries: StackEntryPayload[]) => StackSectionStackEntry[] {
  return mergeBoxStackEntries =>
    mergeBoxStackEntries.map((entry, index) => {
      const staticStatus = staticEffectiveStatus(entry)

      const stackMergeabilityQuery = stackMergeabilityQueries[index]

      if (stackMergeabilityQuery?.isError) {
        return {
          ...entry,
          channels: null,
          effectiveEntryStatus: staticStatus ?? EffectiveEntryStatus.NotReady,
        }
      }

      if (stackMergeabilityQuery?.isLoading || !stackMergeabilityQuery || !stackMergeabilityQuery.data) {
        return {
          ...entry,
          channels: null,
          effectiveEntryStatus:
            staticStatus ?? SEED_PLACEHOLDER_BY_SERVER_STATUS[entry.status] ?? EffectiveEntryStatus.Loading,
        }
      }

      const {channels, statusCheckChannels, effectiveEntryStatus} = stackMergeabilityQuery.data

      return {
        ...entry,
        channels: {...channels, ...statusCheckChannels},
        // EffectiveEntryStatus is only temporarily nullable. When feature
        // `pull_request_stacks_effective_entry_statuses` is fully graduated this
        // should never be null | undefined.
        effectiveEntryStatus: staticStatus ?? (effectiveEntryStatus || undefined),
      }
    })
}
